/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Modal, Button } from "antd";
import { useReactToPrint } from "react-to-print";
import { PiPrinterFill } from "react-icons/pi";
import logo from "../../../assets/images/logo_nsf.png";
import MasterProcessService from "../../../service/ERP/MasterProcess.service";
import dayjs from "dayjs";
import "./mp.css";

const mpService = MasterProcessService();

const groupDetailByStep = (detail = [], remarkSteps = []) => {
  const remarkMap = new Map();
  (remarkSteps || []).forEach((r) =>
    remarkMap.set(Number(r?.stepno || 0), r?.remark || ""),
  );

  const grouped = new Map();
  (detail || []).forEach((item) => {
    const stepNo = Number(item?.stepno || 1) || 1;
    if (!grouped.has(stepNo)) grouped.set(stepNo, []);
    grouped.get(stepNo).push(item);
  });

  return Array.from(grouped.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([stepNo, items]) => ({
      stepNo,
      remark: remarkMap.get(stepNo) || "",
      materials: items
        .slice()
        .sort((a, b) => Number(a?.mpno || 0) - Number(b?.mpno || 0))
        .map((item, idx) => ({ ...item, seq: idx + 1 })),
    }));
};

/* น้ำหนักวัตถุดิบ = (น้ำหนักเข้าสูตร / multiply) / yield (สูตรเดียวกับหน้า Master Process) */
const calQty = (r) => {
  const mul = Number(r?.multiply || 0) > 0 ? Number(r.multiply) : 1;
  const yld = Number(r?.yield || 0) > 0 ? Number(r.yield) : 100;
  return parseFloat((Number(r?.ratio || 0) / mul / (yld / 100)).toFixed(2));
};

export default function MpPrintPreview() {
  const { code, mpid } = useParams();
  const wrapRef  = useRef(null);
  const docRef   = useRef(null);
  const printRef = useRef(null);
  const [header, setHeader] = useState({});
  const [steps, setSteps] = useState([]);

  const handlePrint = useReactToPrint({
    documentTitle: `MP-${code}`,
    removeAfterPrint: true,
  });

  /* ── scale ตาม width ให้ดูใหญ่ขึ้น ── */
  useEffect(() => {
    const fit = () => {
      const wrap   = wrapRef.current;
      const doc    = docRef.current;
      const layout = printRef.current;
      if (!wrap || !doc || !layout) return;
      const availW = wrap.clientWidth - 32;
      const scale  = Math.min(availW / layout.offsetWidth, 1) * 0.85;
      doc.style.transform       = `scale(${scale})`;
      doc.style.transformOrigin = "top center";
      doc.style.marginBottom    = `${(scale - 1) * layout.offsetHeight}px`;
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [header, steps]);

  useEffect(() => {
    mpService.get(code, mpid || null).then((res) => {
      const { header: h, detail, remark_step } = res.data?.data || {};
      setHeader(h || {});
      setSteps(groupDetailByStep(detail, remark_step));
    }).catch((err) => {
      Modal.error({
        title: "Error",
        content: err.response?.data?.message || "Request failed. Please try again.",
        onOk: () => window.close(),
      });
    });
  }, [code, mpid]);

  return (
    <div className="page-show" id="mp" ref={wrapRef}>
      <div className="doc-wrapper" ref={docRef}>
        <div className="print-btn no-print">
          <Button
            className="bn-center bg-blue-400"
            icon={<PiPrinterFill style={{ fontSize: "1.1rem" }} />}
            onClick={() => handlePrint(null, () => wrapRef.current)}
          >
            PRINT
          </Button>
        </div>

        <div className="print-layout" ref={printRef}>

        {/* ── Company Letterhead ── */}
        <div className="print-head">
          <img src={logo} alt="logo" className="head-logo" />
          <div className="head-company">
            <p className="co-name">NINE STAR FOOD CO., LTD.</p>
            <p className="co-addr">99/9 Moo8, Tambon Kongdin, Amphoe Klaeng, Rayong 22160</p>
          </div>
        </div>

        {/* ── Title + Info ── */}
        <div className="print-head-content">
          <div className="title-band">
            <div className="title-center">
              <p className="doc-name">MASTER PROCESS</p>
              <p className="doc-name-en">PRODUCTION MASTER PROCESS</p>
            </div>
            <div className="doc-no-box">
              <span className="doc-no-label">เวอร์ชั่น</span>
              <span className="doc-no-value">{header.version != null ? `v${header.version}` : "-"}</span>
            </div>
          </div>
          <div className="info-grid">
            <div className="info-left">
              <div className="h-group-text">
                <span className="h-label">รหัสสินค้า</span>
                <span className="h-text">{header.fg_code}</span>
              </div>
              <div className="h-group-text">
                <span className="h-label">ชื่อสินค้า</span>
                <span className="h-text">{header.fg_name}</span>
              </div>
              <div className="h-group-text">
                <span className="h-label">Remark</span>
                <span className="h-text" style={{ fontWeight: 400 }}>{header.remark || "-"}</span>
              </div>
            </div>
            <div className="info-right">
              <div className="h-group-text">
                <span className="h-label">อัพเดทล่าสุด</span>
                <span className="h-text">
                  {header.updated_date ? dayjs(header.updated_date).format("DD/MM/YYYY HH:mm") : "-"}
                </span>
              </div>
              <div className="h-group-text">
                <span className="h-label">อัพเดทโดย</span>
                <span className="h-text">{header.updated_by || "-"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Steps ── */}
        <div className="print-content">
          <p className="section-title">ขั้นตอนการทำงานผลิต</p>
          {steps.map((step) => (
            <div className="step-block" key={step.stepNo}>
              <p className="step-title">Step {step.stepNo}</p>
              <table className="tb-step">
                <colgroup>
                  <col style={{ width: "8mm" }} />
                  <col style={{ width: "22mm" }} />
                  <col />
                  <col style={{ width: "17mm" }} />
                  <col style={{ width: "13mm" }} />
                  <col style={{ width: "13mm" }} />
                  <col style={{ width: "18mm" }} />
                  <col style={{ width: "24mm" }} />
                  <col style={{ width: "17mm" }} />
                  <col style={{ width: "11mm" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>รหัสวัตถุดิบ</th>
                    <th style={{ textAlign: "left", paddingLeft: "2mm" }}>ชื่อวัตถุดิบ</th>
                    <th>น้ำหนักเข้าสูตร</th>
                    <th>Multiply</th>
                    <th>Yield (%)</th>
                    <th>น้ำหนักวัตถุดิบ</th>
                    <th>ขั้นตอน</th>
                    <th>ชนิดวัตถุดิบ</th>
                    <th>หน่วย</th>
                  </tr>
                </thead>
                <tbody>
                  {step.materials.map((m, i) => (
                    <tr key={m.id || i}>
                      <td className="txt-c">{m.seq}</td>
                      <td className="txt-c">{m.stcode}</td>
                      <td className="txt-l">{m.stname}</td>
                      <td className="txt-r">{calQty(m).toFixed(2).toLocaleString()}</td>
                      <td className="txt-c">{m.multiply}</td>
                      <td className="txt-c">{m.yield}</td>
                      <td className="txt-r">{m.ratio}</td>
                      <td className="txt-l">{m.method}</td>
                      <td className="txt-c">{m.sttype}</td>
                      <td className="txt-c">{m.unit}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {step.remark && (
                <p className="step-remark">
                  <b>การทำงาน:</b> {step.remark}
                </p>
              )}
            </div>
          ))}
        </div>

        </div>
      </div>
    </div>
  );
}
